import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { OrderItem } from '@/models/order';
import { createOrdersPageHeaders } from '@/pages/admin/orders/OrdersPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { orderService } from '@/services/orderService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IOrderFilters, IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'createdAt', order: 'desc' as const }];

function createOrderStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<OrderItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IOrderFilters>({
        sportId: createRequiredSportFilter(),
        betOrderDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'orders.orderDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betOrderId: {
            value: null,
            title: 'orders.orderId',
            allowSaveState: false,
        },
        betOrderUserId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        userPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
        betOrderCustomTeamId: {
            value: null,
            title: 'common.customTeam',
            allowSaveState: true,
        },
        betOrderStatusId: {
            value: null,
            title: 'orders.status',
            allowSaveState: true,
        },
        betOrderEventPeriodId: {
            value: null,
            title: 'orders.market',
            allowSaveState: true,
        },
        betOrderSelectionId: {
            value: null,
            title: 'orders.betType',
            allowSaveState: true,
        },
        betOrderEventTypeId: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.betOrderDate.value = todayDateRangeInUserTimeZone();
        filters.betOrderId.value = null;
        filters.betOrderUserId.value = null;
        filters.userPlatformId.value = null;
        filters.betOrderCustomTeamId.value = null;
        filters.betOrderStatusId.value = null;
        filters.betOrderEventPeriodId.value = null;
        filters.betOrderSelectionId.value = null;
        filters.betOrderEventTypeId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createOrdersPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'order',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.betOrderId.value) {
                filtersToSend.betOrderDate = { ...filtersToSend.betOrderDate, value: null };
            }

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Agent', 'Country', 'AuditTable'],
            };
            const response = await orderService.list(params);
            items.value = response.data.rows.map((row) => new OrderItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
    };
}

export function useOrderStore(storeId: string) {
    const useStore = defineStore(storeId, () => createOrderStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'order'),
    });
    return useStore();
}

export type OrderStoreSetup = ReturnType<typeof useOrderStore>;
